/**
 * First-wins conversion claims within a single page context.
 *
 * Multiple auto-detection sources can observe the same conversion (the
 * e-commerce platform detector and the GA4 dataLayer bridge both see a
 * Shopify purchase). Whoever claims first sends; the other skips. Cross-
 * pageload duplicates are handled separately by the deterministic
 * "ord_<order_id>" event id (DB unique index + platform-side dedup).
 */

import { debugLog } from "./state.js";

const claimed: Record<string, true> = {};

/** Returns true when this caller may send the conversion (first claim). */
export function claimConversion(eventName: string, key: string): boolean {
  const claimKey = eventName + "|" + key;
  if (claimed[claimKey]) {
    debugLog("conversionClaims", "already claimed", claimKey);
    return false;
  }
  claimed[claimKey] = true;
  return true;
}

/**
 * Deterministic event id for order-based conversions.
 *
 * Shopify's order id arrives as either `gid://shopify/OrderIdentity/5210499102`
 * or a bare `5210499102`, depending on the store, with no documented rule.
 * Both shapes have to collapse to the same key or the same order is counted
 * twice: once from here and once from the webhook path, which normalises it the
 * same way. See webhook-dedupe-key.ts.
 */
export function orderEventId(orderId: string | undefined): string | undefined {
  if (!orderId) return undefined;
  const bare = String(orderId).replace(/^gid:\/\/shopify\/[A-Za-z]+\//, "");
  return "ord_" + bare.slice(0, 64);
}
